import React, { createContext, useContext, useEffect, useState } from 'react';
import ApiService from 'services/ApiService';
import { repeatEffect, settingsConfig } from 'utils/commonFunc';
import { ResponsivePie } from '@nivo/pie';
import { useNavigate } from 'react-router-dom';
import module from 'pages/css/moduleStyle.module.css';
import styles from 'pages/css/trfSfty.module.css';
import { constructFromSymbol } from 'date-fns/constants';
import { useTranslation } from 'react-i18next';

export const TrafficTotalContext = createContext(null);

export const EVENT_CODE_MAP = {
    4358: '정지선 위반',
    4450: '꼬리물기',
    4387: '무단횡단',
    4417: '역주행',
    4418: '정지차량',
    4595: '사고'
};

export function SwItsGetEvntDetailProvider({ crsrdId, children }) {
    const [currentData, setCurrentData] = useState();
    const [selectedEvent, setSelectedEvent] = useState(null); // 스냅샷관리용 

    useEffect(() => {
        return repeatEffect(async () => {
            const response = await ApiService.get("newdashboard/trfsfty/GetEvntAll");
            if (response.status === 200) {
                const data = response.data;
                const outEvnt = data.unxp;
                const evnt = data.evnt;

                // 공통 구조로 통합
                const mergeDetail = (rawData, crsrdId) => {
                    if (!rawData || typeof rawData !== 'object') return {};

                    return Object.entries(rawData).reduce((acc, [cd, evntData]) => {
                        const detail = crsrdId && evntData.detail?.[crsrdId]
                            ? { [crsrdId]: evntData.detail[crsrdId] }
                            : evntData.detail;

                        const totalCnt = crsrdId
                            ? evntData.detail?.[crsrdId]?.cnt || 0
                            : Object.values(evntData.detail || {}).reduce((sum, item) => sum + item.cnt, 0);

                        if (detail) {
                            acc[cd] = {
                                ...evntData,
                                crsrdCnt: crsrdId ? 1 : Object.keys(detail).length,
                                totalCnt,
                                detail,
                            };
                        } else {
                            acc[cd] = {
                                ...evntData,
                                crsrdCnt: 0,
                                totalCnt: 0,
                                detail: {},
                            };
                        }

                        return acc;
                    }, {});
                };

                const merged = {
                    ...mergeDetail(outEvnt, crsrdId),
                    ...mergeDetail(evnt, crsrdId),
                };

                // 0.5초 지연 후 데이터 업데이트
                setTimeout(() => {
                    setCurrentData({
                        updateTime: data.updateTime,
                        totalCnt: data.totalCnt,
                        unxpDetail: merged,
                    });
                }, 500);
            }
        }, settingsConfig().contentUpdateDelaySec);
    }, [crsrdId]);

    // return (
    //     <TrafficTotalContext.Provider value={currentData}>
    //         {children}
    //     </TrafficTotalContext.Provider>
    // );
    return (
        <TrafficTotalContext.Provider value={{
            data: currentData,
            selectedEvent,
            setSelectedEvent
        }}>
            {children}
        </TrafficTotalContext.Provider>
    );
}

// 대시보드 교통안전 그리드
export function SwItsGetEvntDetailInfo({ isInclude = true, targetCodes = [] }) {
    const { data: currentData, selectedEvent, setSelectedEvent } = useContext(TrafficTotalContext);
    if (!currentData || !currentData.unxpDetail) return null;

    const allEvents = Object.values(currentData.unxpDetail);

    let filteredEvents = allEvents.filter(event =>
        targetCodes.includes(Number(event.cd))
    );

    if (filteredEvents.length === 0 && targetCodes.length > 0) {
        filteredEvents = targetCodes.map(code => ({
            cd: code,
            cdNm: EVENT_CODE_MAP[code] || `코드 ${code}`,
            totalCnt: 0,
            crsrdCnt: 0,
        }));
    }

    const colorMapTrue = {
        4358: 'var(--chart-05)',
        4450: 'var(--chart-06)',
        4418: 'var(--chart-07)',
    };

    const colorMapFalse = {
        4417: 'var(--chart-04)',
        4387: 'var(--chart-02)',
        4595: 'var(--chart-03)',
    };

    const colorMap = isInclude ? colorMapTrue : colorMapFalse;

    const sortedEvents = filteredEvents.sort((a, b) => b.totalCnt - a.totalCnt);

    return (
        <>
            {sortedEvents.map((event) => {
                const bgColor = colorMap[event.cd] || 'var(--gray-300)'; // 매칭 없으면 기본색
                const displayCrsrdCnt = event.totalCnt === 0 ? 0 : event.crsrdCnt;
                return (
                    <div key={event.cd} className="unxpLi flx j_spc_btw a_center" style={{ boxShadow: `inset 0 0 0 2px ${bgColor}` }}>
                        <span className="titBtn" style={{ background: bgColor }}>
                            {event.cdNm}
                        </span>
                        <div className="flx a_center a_flx_end">
                            <p className="unxpFgr flx a_flx_end">
                                {event.totalCnt?.toLocaleString()}
                                <span className='unxpSubFgr'>건/시</span>
                            </p>
                            <span className='sls'>|</span>
                            <p className="unxpFgr flx a_flx_end" style={{ fontSize: '40px', fontWeight: 'var(--ft-Medium)', color: 'var(--white-50)' }}>
                                {displayCrsrdCnt}
                                <span className='unxpSubFgr'>개소</span>
                            </p>
                        </div>
                    </div>
                );
            })}
        </>
    );
}


export function SwItsGetEvntDetailModule({ targetCodes = [], showTotal = false }) {
    const { data: currentData, selectedEvent, setSelectedEvent } = useContext(TrafficTotalContext);
    if (!currentData || !currentData.unxpDetail) return null;

    const allEvents = Object.values(currentData.unxpDetail);

    const EVENT_CODE_MAP = {
        4358: '정지선 위반',
        4450: '꼬리물기',
        4418: '정지차량',
        4417: '역주행',
        4387: '무단횡단',
        4595: '사고',
    };

    const colorMap = {
        4358: 'var(--chart-05)', // 정지선 위반
        4450: 'var(--chart-06)', // 꼬리물기
        4418: 'var(--chart-07)', // 정지차량
        4417: 'var(--chart-04)', // 역주행
        4387: 'var(--chart-02)', // 무단횡단
        4595: 'var(--chart-03)', // 사고
    };

    const fixedTargetCodes = [4358, 4450, 4418, 4417, 4387, 4595];

    let filteredEvents = allEvents.filter(event =>
        fixedTargetCodes.includes(Number(event.cd))
    );

    if (filteredEvents.length === 0) {
        filteredEvents = fixedTargetCodes.map(code => ({
            cd: code,
            cdNm: EVENT_CODE_MAP[code] || `코드 ${code}`,
            totalCnt: 0,
            crsrdCnt: 0,
        }));
    }

    // total
    const totalEvent = {
        cd: 'total',
        cdNm: '전체',
        totalCnt: filteredEvents.reduce((acc, curr) => acc + curr.totalCnt, 0),
        crsrdCnt: filteredEvents.reduce((acc, curr) => acc + curr.crsrdCnt, 0),
    };

    const sortedEvents = filteredEvents.sort((a, b) => b.totalCnt - a.totalCnt);
    return (
        <>
            {showTotal && (
                <div className="unxpLi totalBox flx j_spc_btw a_center" style={{ border: '2px solid var(--white-50)' }}>
                    <span className="titBtn" style={{ background: 'var(--white-50)', color: 'var(--white)', fontWeight: 'var(--ft-Medium)' }}>
                        {totalEvent.cdNm}
                    </span>
                    <div className="flx a_center a_flx_end">
                        <p className="unxpFgr flx a_flx_end">
                            {totalEvent.totalCnt === 0 ? '0' : totalEvent.totalCnt?.toLocaleString()}
                            <span className='unxpSubFgr'>건/시</span>
                        </p>
                        <span className='sls'>|</span>
                        <p className="unxpFgr flx a_flx_end" style={{ fontSize: '40px', fontWeight: 'var(--ft-Medium)', color: 'var(--white-50)' }}>
                            {totalEvent.totalCnt === 0 ? '0' : totalEvent.crsrdCnt}
                            <span className='unxpSubFgr'>개소</span>
                        </p>
                    </div>
                </div>
            )}

            {sortedEvents.map((event) => {
                const bgColor = colorMap[event.cd] || 'var(--gray-400)';
                return (
                    <div key={event.cd} className="unxpLi flx j_spc_btw a_center" style={{ boxShadow: `inset 0 0 0 2px ${bgColor}` }}>
                        <span className="titBtn" style={{ background: bgColor }}>
                            {event.cdNm}
                        </span>
                        <div className="flx a_center a_flx_end">
                            <p className="unxpFgr flx a_flx_end">
                                {event.totalCnt === 0 ? '0' : event.totalCnt?.toLocaleString()}
                                <span className='unxpSubFgr'>건/시</span>
                            </p>
                            <span className='sls'>|</span>
                            <p className="unxpFgr flx a_flx_end" style={{ fontSize: '40px', fontWeight: 'var(--ft-Medium)', color: 'var(--white-50)' }}>
                                {event.totalCnt === 0 ? '0' : event.crsrdCnt}
                                <span className='unxpSubFgr'>개소</span>
                            </p>
                        </div>
                    </div>
                );
            })}
        </>
    );
}


export function SwItsGetEvntDetailTotalCntInfo({ labelClass = 'boxTit', valueClass = 'subTit', valueClass1 = 'point' }) {
    const { data: currentData, selectedEvent, setSelectedEvent } = useContext(TrafficTotalContext);
     const { i18n, t } = useTranslation();
    if (!currentData || !currentData.unxpDetail) return null;

    return (
        <>
             <span 
                className={`${labelClass}${i18n.language === 'en' ? '_en' : ''}`} 
                style={{ color: 'var(--iv-03)' }}
            >
                {t('IntersectionIncidents')}
            </span>
        </>
    );
}

export function SwItsGetEvntDetailDetectedTotalCntInfo({ labelClass = 'boxTit', valueClass = 'subTit', valueClass1 = 'point' }) {
    const { data: currentData, selectedEvent, setSelectedEvent } = useContext(TrafficTotalContext);
      const { i18n, t } = useTranslation();
    if (!currentData || !currentData.unxpDetail) return null;

    return (
        <>
            <span  className={`${labelClass}${i18n.language === 'en' ? '_en' : ''}`}  style={{ color: 'var(--iv-04)' }}> {t('IntersectionUnsafeBehaviors')}
               </span>{' '}
        </>
    );
}

// 
export function SwItsGetEvntDetailChart({
    className = `${module.mdulChart}`,
    isInclude = true,
    targetCodes = []
}) {
    const { data: currentData, selectedEvent, setSelectedEvent } = useContext(TrafficTotalContext);
    if (!currentData || !currentData.unxpDetail) return null;

    const onlyDetectCodes = [4358, 4450, 4418];
    const outBreakCodes = [4417, 4387, 4595];

    const colorMapTrue = {
        4358: 'var(--chart-05)',
        4450: 'var(--chart-06)',
        4418: 'var(--chart-07)',
    };

    const colorMapFalse = {
        4417: 'var(--chart-04)',
        4387: 'var(--chart-02)',
        4595: 'var(--chart-03)',
    };

    const colorMap = isInclude ? colorMapTrue : colorMapFalse;

    const unxpData = Object.values(currentData.unxpDetail);

    const WDHD_CODE = 4387;
    const YJGJ_CODE = 4417;

    let chartEvents = [];

    if (isInclude) {
        chartEvents = unxpData.filter(event =>
            targetCodes.includes(Number(event.cd))
        );

        if (chartEvents.length === 0 && targetCodes.length > 0) {
            chartEvents = targetCodes.map(code => ({
                cd: code,
                cdNm: EVENT_CODE_MAP[code] || `코드 ${code}`,
                totalCnt: 0,
            }));
        }
    } else {
        const filteredAll = unxpData.filter(e => !onlyDetectCodes.includes(Number(e.cd)));
        const wdhd = filteredAll.find(e => Number(e.cd) === WDHD_CODE);
        const yjgj = filteredAll.find(e => Number(e.cd) === YJGJ_CODE);

        chartEvents = [];

        if (wdhd) {
            chartEvents.push({
                cd: WDHD_CODE,
                cdNm: EVENT_CODE_MAP[WDHD_CODE],
                totalCnt: wdhd.totalCnt,
            });
        }

        if (yjgj) {
            chartEvents.push({
                cd: YJGJ_CODE,
                cdNm: EVENT_CODE_MAP[YJGJ_CODE],
                totalCnt: yjgj.totalCnt,
            });
        }
    }

    // totalCnt 기준 내림차순 정렬
    const sortedChartEvents = chartEvents.sort((a, b) => b.totalCnt - a.totalCnt);

    const totalSum = sortedChartEvents.reduce((sum, item) => sum + item.totalCnt, 0);

    const chartData = totalSum === 0
        ? [{
            id: '데이터 없음',
            label: '데이터 없음',
            value: 1,
            color: 'var(--gray-400)',
        }]
        : sortedChartEvents.map((item) => ({
            id: item.cdNm,
            label: item.cdNm,
            value: item.totalCnt,
            color: colorMap[item.cd] || 'var(--gray-300)', // 매칭 없으면 기본색
        }));

    return (
        <div className={className}>
            <ResponsivePie
                data={chartData}
                animate={true}
                margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                innerRadius={0.5}
                padAngle={1.5}
                cornerRadius={3}
                colors={({ data }) => data.color}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor="#999"
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 3]] }}
                enableArcLinkLabels={false}
                enableArcLabels={false}
                tooltip={({ datum }) => (
                    <div
                        style={{
                            padding: '6px 9px',
                            background: '#fff',
                            color: '#333',
                            border: `1px solid ${datum.color}`,
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    >
                        {datum.id}: {totalSum === 0 ? '없음' : `${datum.value.toLocaleString()}건`}
                    </div>
                )}
            />
        </div>
    );
}

// 통합관제 - 교통안전 - 돌발 위험상황 - 차트 데이터
export function SwItsGetEvntDetailChartModule({ className = `${module.mdulChart}` }) {
    const { data: currentData, selectedEvent, setSelectedEvent } = useContext(TrafficTotalContext);
    if (!currentData || !currentData.unxpDetail) return null;

    const unxpData = Object.values(currentData.unxpDetail);

    const EVENT_CODE_MAP = {
        4358: '정지선 위반',
        4450: '꼬리물기',
        4418: '정지차량',
        4417: '역주행',
        4387: '무단횡단',
        4595: '사고',
    };

    const colorMap = {
        4358: 'var(--chart-05)', // 정지선 위반
        4450: 'var(--chart-06)', // 꼬리물기
        4418: 'var(--chart-07)', // 정지차량
        4417: 'var(--chart-04)', // 역주행
        4387: 'var(--chart-02)', // 무단횡단
        4595: 'var(--chart-03)', // 사고
    };

    const fixedTargetCodes = [4358, 4450, 4418, 4417, 4387, 4595];

    const categorized = fixedTargetCodes.map(code => {
        const match = unxpData.find(e => Number(e.cd) === code);
        return {
            id: EVENT_CODE_MAP[code] || `코드 ${code}`,
            label: EVENT_CODE_MAP[code] || `코드 ${code}`,
            value: match?.totalCnt || 0,
            code: code,
        };
    });

    const totalSum = categorized.reduce((sum, item) => sum + item.value, 0);

    const sortedEvents = categorized.sort((a, b) => b.value - a.value);

    const chartData = totalSum === 0
        ? [{
            id: '데이터 없음',
            label: '데이터 없음',
            value: 1,
            color: 'var(--gray-400)',
        }]
        : sortedEvents.map(item => ({
            ...item,
            color: colorMap[item.code] || 'var(--gray-300)',
        }));

    return (
        <div className={className}>
            <ResponsivePie
                data={chartData}
                animate={true}
                margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                innerRadius={0.5}
                padAngle={1.5}
                cornerRadius={3}
                colors={({ data }) => data.color}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor="#999"
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 3]] }}
                enableArcLinkLabels={false}
                enableArcLabels={false}
                tooltip={({ datum }) => (
                    <div
                        style={{
                            padding: '6px 9px',
                            background: '#fff',
                            color: '#333',
                            border: `1px solid ${datum.color}`,
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    >
                        {datum.id}: {totalSum === 0 ? '없음' : `${datum.value.toLocaleString()}건`}
                    </div>
                )}
            />
        </div>
    );
}

export function SwItsGetEvntDetailCrsrd({ selectedEvent, eventDetail, data }) {
    const colorMap = {
        4358: 'var(--chart-05)', // 정지선 위반
        4450: 'var(--chart-06)', // 꼬리물기
        4418: 'var(--chart-07)', // 정지차량
        4417: 'var(--chart-04)', // 역주행
        4387: 'var(--chart-02)', // 무단횡단
        4595: 'var(--chart-03)', // 사고
    };

    if (!selectedEvent || !eventDetail) {
        return <div className={module.noData}>선택된 이벤트 없음</div>;
    }

    const ALLOWED_CODES = [4358, 4450, 4418, 4417, 4387, 4595];

    if (selectedEvent.cd !== 'total' && !ALLOWED_CODES.includes(Number(selectedEvent.cd))) {
        return <div className={module.noData}>허용되지 않은 이벤트 코드입니다.</div>;
    }

    // 1. 교차로별 이벤트 코드 매핑 생성
    const crsrdEventMap = {};

    if (data?.unxpDetail) {
        Object.entries(data.unxpDetail).forEach(([codeStr, eventGroup]) => {
            const code = Number(codeStr);
            if (!ALLOWED_CODES.includes(code)) return;

            const detail = eventGroup.detail || {};
            Object.entries(detail).forEach(([crsrdId, info]) => {
                // crsrdNm 없거나 cnt가 0이면 무시
                if (!info.crsrdNm || !info.cnt) return;

                if (!crsrdEventMap[crsrdId]) {
                    crsrdEventMap[crsrdId] = {
                        crsrdId,
                        crsrdNm: info.crsrdNm,
                        cnt: info.cnt,
                        codes: [code],
                    };
                } else {
                    crsrdEventMap[crsrdId].cnt += info.cnt;
                    if (!crsrdEventMap[crsrdId].codes.includes(code)) {
                        crsrdEventMap[crsrdId].codes.push(code);
                    }
                }
            });
        });
    }
    const selectedCd = Number(selectedEvent.cd);
    const detailListRaw = Object.values(crsrdEventMap);

    // 0건이면 기존 정렬 유지
    const detailList =
        selectedEvent.totalCnt === 0
            ? detailListRaw.sort((a, b) => b.cnt - a.cnt)
            : detailListRaw.sort((a, b) => {
                const aHasCode = a.codes.includes(selectedCd);
                const bHasCode = b.codes.includes(selectedCd);

                // selectedCd 포함된 것 우선 → 포함 둘 다 or 둘 다 아니면 cnt로 정렬
                if (aHasCode && !bHasCode) return -1;
                if (!aHasCode && bHasCode) return 1;
                return b.cnt - a.cnt;
            });
    
    return (
        <div className={`${styles.unxpList} L_2m_20 flx flx_col`}>
            <div className={`${styles.subTitle} subTit flx j_spc_btw`}>
                {/* 제목 영역 생략 */}
            </div>

            <ul className={`${styles.jayWalk} cst_scroll flx flx_col`}>
                {detailList.map((item) => (
                    <li key={item.crsrdId} className={`${styles.selectList}`}>
                        <button
                            type="button"
                            className="flx flx_col j_center a_flx_st"
                        >
                            <div className="flx j_spc_btw a_center">
                                <span>{item.crsrdNm}</span>
                                <span className='flx a_center gap8'>
                                    {item.codes.map((code) => (
                                        <span
                                            key={code}
                                            className={styles.circle_trfSfty}
                                            style={{ background: colorMap[code] || '#ccc'}}
                                        >
                                        </span>
                                    ))}
                                    <span className="flx a_center" style={{marginLeft:'20px'}}>
                                        <span className={styles.alert50_trfSfty}>{item.cnt?.toLocaleString()}</span>
                                        <span className={styles.alert50unit_trfSfty}>건</span>
                                    </span>
                                </span>
                            </div>
                            <span className={`${styles.crsrdID}`}>ID : {item.crsrdId}</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}


export function SwItsGetEvntDetailInfoList() {
    const { data: currentData, selectedEvent, setSelectedEvent } = useContext(TrafficTotalContext);
    const [eventDetail, setEventDetail] = useState(null);
    const unxpArray = currentData?.unxpDetail
        ? Object.values(currentData.unxpDetail).sort((a, b) => b.totalCnt - a.totalCnt)
        : [];

    useEffect(() => {
        if (!selectedEvent && unxpArray.length > 0) {
            setSelectedEvent(prev => prev ?? unxpArray[0]);
        }
    }, [unxpArray]);
    useEffect(() => {
        if (selectedEvent && selectedEvent.detail) {
            setEventDetail(selectedEvent.detail);
        }
    }, [selectedEvent]);

    return (
        <>
            <div className='flx j_spc_btw' style={{ height: '100%' }}>
                <div className={`${styles.evntBox} L_2m_20`}>
                    <div className={`${module.gap20} ${styles.unxpWrap_trfSfty}`}>
                        <div className={`${styles.unxpchart_trfSfty} unxpchart`}>
                            <SwItsGetEvntDetailChartModule />
                        </div>
                        <div className={`${styles.unxpGrid_trfSfty} unxpGrid flx flx_col j_spc_btw`}>
                            <SwItsGetEvntDetailList selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent} />
                        </div>
                    </div>
                </div>

                {eventDetail ? (
                    // <SwItsGetEvntDetailCrsrd selectedEvent={selectedEvent} eventDetail={eventDetail} />
                    <SwItsGetEvntDetailCrsrd selectedEvent={selectedEvent} eventDetail={eventDetail} data={currentData} />
                ) : (
                    <div>상세 정보를 불러오는 중...</div>
                )}
            </div>
        </>
    );
}

export function SwItsGetEvntDetailList() {

    // const currentData = useContext(TrafficTotalContext);
    const { data: currentData, selectedEvent, setSelectedEvent } = useContext(TrafficTotalContext);
    if (!currentData || !currentData.unxpDetail) return <p>이벤트 데이터 없음</p>;

    const allEvents = Object.values(currentData.unxpDetail);

    const EVENT_CODE_MAP = {
        4358: '정지선 위반',
        4450: '꼬리물기',
        4418: '정지차량',
        4417: '역주행',
        4387: '무단횡단',
        4595: '사고',
    };

    const colorMap = {
        4358: 'var(--chart-05)', // 정지선 위반
        4450: 'var(--chart-06)', // 꼬리물기
        4418: 'var(--chart-07)', // 정지차량
        4417: 'var(--chart-04)', // 역주행
        4387: 'var(--chart-02)', // 무단횡단
        4595: 'var(--chart-03)', // 사고
    };

    const fixedTargetCodes = [4358, 4450, 4418, 4417, 4387, 4595];

    const categorizedEvents = fixedTargetCodes.map(code => {
        const match = allEvents.find(e => Number(e.cd) === code);
        return {
            cd: code,
            cdNm: EVENT_CODE_MAP[code] || `코드 ${code}`,
            totalCnt: match?.totalCnt || 0,
            crsrdCnt: match?.crsrdCnt || 0,
            detail: match?.detail || {},
        };
    });

    const totalEvent = {
        cd: 'total',
        cdNm: '전체',
        totalCnt: categorizedEvents.reduce((acc, curr) => acc + curr.totalCnt, 0),
        crsrdCnt: categorizedEvents.reduce((acc, curr) => acc + curr.crsrdCnt, 0),
    };

    const sortedEvents = [totalEvent, ...categorizedEvents.sort((a, b) => b.totalCnt - a.totalCnt)];

    return (
        <div className={`${styles.eventBoxWrap_trfSfty} eventBoxWrap`}>
            {sortedEvents.map((event) => {
                const isTotal = event.cd === 'total';
                const bgColor = isTotal ? 'var(--white-50)' : colorMap[event.cd] || 'var(--gray-300)';
                const isActive = selectedEvent?.cd === event.cd;
                const displayCrsrdCnt = event.totalCnt === 0 ? 0 : event.crsrdCnt;

                return (
                    <div
                        key={event.cd}
                        className={`unxpLi flx j_spc_btw a_center ${isActive ? 'activeEventBox' : ''}`}
                        style={{
                            boxShadow: `inset 0 0 0 2px ${bgColor}`,
                            background: isActive ? 'rgba(255,255,255,0.05)' : 'transparent',
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => setSelectedEvent(event)}
                            className="unxpLflxBtn flx j_spc_btw a_center"
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            <span className="titBtn" style={{
                                background: bgColor,
                            }}>
                                {event.cdNm}
                            </span>
                            <div className="flx a_center a_flx_end">
                                <p className="unxpFgr flx a_flx_end">
                                    {event.totalCnt?.toLocaleString()}
                                    <span className="unxpSubFgr">건/시</span>
                                </p>
                                <span className="subTxt sls">|</span>
                                <p className="unxpFgr flx a_flx_end" style={{
                                    fontSize: '40px',
                                    fontWeight: 'var(--ft-Medium)',
                                    color: 'var(--white-50)',
                                }}>
                                    {displayCrsrdCnt}
                                    <span className="unxpSubFgr">개소</span>
                                </p>
                            </div>
                        </button>
                    </div>
                );
            })}
        </div>

    );
}


const cctvCapture = [
    { name: '역주행', img: '/images/event/ReverseRun.jpg' },
    { name: '무단횡단', img: '/images/event/jaywalking.jpg' },
    { name: '정지차량', img: '/images/event/stopVeh.png' },
    { name: '사고', img: '/images/event/accident.png' },
    { name: '정지선 위반', img: '/images/event/stopLineViolation.jpg' },
    { name: '꼬리물기', img: '/images/event/Tailgating.jpg' }
];
// 역주행 이미지 목록 
const reverseRun = [
    { name: '역주행(성균관대사거리)', img: '/images/event/ReverseRun1.jpg' },
    { name: '역주행(경기아트센터사거리)', img: '/images/event/ReverseRun2.jpg' },
    { name: '역주행(교동사거리)', img: '/images/event/ReverseRun3.jpg' },
    { name: '역주행(매탄권선역사거리)', img: '/images/event/ReverseRun4.jpg' },
]
// 무단횡단 이미지 목록
const jaywalking = [
    { name: '무단횡단(센트럴파크로사거리)', img: '/images/event/jaywalking1.jpg' },
    { name: '무단횡단(수성중사거리)', img: '/images/event/jaywalking2.jpg' },
    { name: '무단횡단(신동사거리)', img: '/images/event/jaywalking3.jpg' },
    { name: '무단횡단(청솔마을사거리)', img: '/images/event/jaywalking4.jpg' },
]
// 정지차량 이미지 목록
const stopVeh = [
    { name: '정지차량(교동사거리)', img: '/images/event/stopVeh1.jpg' },
    { name: '정지차량(법원사거리)', img: '/images/event/stopVeh2.jpg' },
    { name: '정지차량(컨벤션센터사거리)', img: '/images/event/stopVeh3.png' },
    { name: '정지차량(만석공원사거리)', img: '/images/event/stopVeh4.jpg' },
]
// 사고 이미지 목록 
const accident = [
    { name: '사고(오목천교사거리)', img: '/images/event/accident1.jpg' },
    { name: '사고(경기아트센터사거리)', img: '/images/event/accident2.png' },
    { name: '사고(성대역사거리)', img: '/images/event/accident3.jpg' },
    { name: '사고(신매탄사거리)', img: '/images/event/accident4.png' },
]
// 정지선 위반 이미지 목록
const stopLineViolation = [
    { name: '정지선 위반(망포역사거리)', img: '/images/event/stopLineViolation1.jpg' },
    { name: '정지선 위반(벌터교차로)', img: '/images/event/stopLineViolation2.jpg' },
    { name: '정지선 위반(솔대사거리)', img: '/images/event/stopLineViolation3.jpg' },
    { name: '정지선 위반(오목천교사거리)', img: '/images/event/stopLineViolation4.jpg' },
]
// 꼬리물기 이미지 목록 
const tailgating = [
    { name: '꼬리물기(효성사거리)', img: '/images/event/Tailgating1.jpg' },
    { name: '꼬리물기(권선지하차도사거리)', img: '/images/event/Tailgating2.jpg' },
    { name: '꼬리물기(도청오거리)', img: '/images/event/Tailgating3.jpg' },
    { name: '꼬리물기(수성중사거리)', img: '/images/event/Tailgating4.jpg' },
]
export function SwItsGetEvntDetailSnap() {
    const { data: currentData, selectedEvent } = useContext(TrafficTotalContext);
    if (!selectedEvent) return null;

    const eventImageMap = {
        '역주행': reverseRun,
        '무단횡단': jaywalking,
        '정지차량': stopVeh,
        '사고': accident,
        '정지선 위반': stopLineViolation,
        '꼬리물기': tailgating,
    };

    const defaultEvents = ['정지선 위반', '무단횡단', '꼬리물기', '정지차량'];

    // 전체일 때
    if (selectedEvent.cd === 'total') {
        const displayImages = defaultEvents
            .map(eventName => {
                const eventImages = eventImageMap[eventName];
                return eventImages?.[0] || null; // 각 이벤트에서 대표 이미지 1개씩
            })
            .filter(Boolean);

        return (
            <ul className={`${styles.cctvCapture_trfSfty} flx`}>
                {displayImages.map((item, idx) => (
                    <li key={idx}>
                        <div className="innerBg">{item.name}</div>
                        <img src={item.img} alt={item.name} />
                    </li>
                ))}
            </ul>
        );
    }

    const imageList = eventImageMap[selectedEvent.cdNm] || [];
    const displayImages = imageList.slice(0, 4); // 최대 4개

    return (
        <ul className={`${styles.cctvCapture_trfSfty} flx`}>
            {displayImages.map((item, idx) => (
                <li key={idx}>
                    <div className="innerBg">{item.name}</div>
                    <img src={item.img} alt={item.name} />
                </li>
            ))}
        </ul>
    );
}
